import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { CalendarBlank, Rows, FunnelSimple, FileArrowDown, LinkSimple, X, Package, ArrowClockwise, CaretLeft, CaretRight, ArrowLeft, MagnifyingGlass, UploadSimple, CheckCircle, WarningCircle, ClockCounterClockwise, Trash, MagicWand, FilePdf, FileCsv, FileXls, ArrowSquareOut, Trophy } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { getIngestions, getIngestionDetail, getSignedDocUrl, detectBackfillIngestions, startDiaryReingest, startDiaryReingestFile, getDiaryReingestStatus, listDiaryReingest, cancelDiaryReingest, inspectDiarySeatMatrix, deleteDiarySeatMatrix, diaryIngestFees, adminCollegesLiteSearch, assignDiaryFee, listAllotmentUnmatched, assignAllotmentUnmatched, llmMatchAllotmentUnmatched, allotmentReconOpen, smReviewOpen } from "../lib/api";
import AgentChat from "./AgentChat";
import IngestReconViewer from "./IngestReconViewer";
import { toast } from "sonner";

const DOC_TYPE_COLORS = {
  allotment: "bg-emerald-100 text-emerald-700",
  full_seat_matrix: "bg-sky-100 text-sky-700",
  provisional_seat_matrix: "bg-indigo-100 text-indigo-700",
  stray_seat_matrix: "bg-amber-100 text-amber-700",
  nmc_seat_matrix: "bg-violet-100 text-violet-700",
  merit_list: "bg-pink-100 text-pink-700",
  schedule: "bg-teal-100 text-teal-700",
  information: "bg-zinc-100 text-zinc-600",
  fees: "bg-rose-100 text-rose-700",
  other: "bg-zinc-100 text-zinc-500",
};
const EXAM_LABELS = { ug_neet: "UG NEET", pg_neet: "PG NEET", ug_srs: "UG SRS", other_exam: "Other Exam" };
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const RANGES = [["7", "7 days"], ["30", "30 days"], ["90", "3 months"], ["180", "6 months"], ["all", "All"]];

const pad = (n) => String(n).padStart(2, "0");
const fmtKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };

function presetRange(range) {
  if (range === "all") return { date_from: "", date_to: "" };
  const days = { "7": 7, "30": 30, "90": 90, "180": 180 }[range] || 30;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { date_from: fmtKey(from), date_to: fmtKey(to) };
}

const Chip = ({ children, cls, size = "text-[9px]" }) => (
  <span className={`inline-flex items-center px-1.5 py-0.5 rounded ${size} font-bold uppercase tracking-wider ${cls}`}>{children}</span>
);

// Small file-type glyph derived from the source format (pdf / csv / xlsx). Replaces the old
// "(CSV)"/"(XLSX)" text suffix so the row shows the input format at a glance.
const FileTypeIcon = ({ format, size = 15 }) => {
  const f = (format || "pdf").toLowerCase();
  const map = {
    csv: { Icon: FileCsv, cls: "text-emerald-600", label: "CSV file" },
    xlsx: { Icon: FileXls, cls: "text-green-700", label: "Excel file" },
    pdf: { Icon: FilePdf, cls: "text-rose-600", label: "PDF file" },
  };
  const { Icon, cls, label } = map[f] || map.pdf;
  return <Icon size={size} weight="fill" className={`${cls} shrink-0`} title={label} aria-label={label} />;
};

// Filename text color per source format (matches the file-type icon palette).
const fileFmtColor = (format) => ({
  csv: "text-emerald-700", xlsx: "text-green-800", pdf: "text-rose-700",
}[(format || "pdf").toLowerCase()] || "text-rose-700");

const SCOPES = [["full", "Full (Allotments + SM)"], ["allot", "Allotments only"], ["sm", "Seat Matrix only"]];
const FILE_STATUS_CLS = {
  completed: "text-emerald-600", processing: "text-[#6043EF]", error: "text-rose-600", queued: "text-zinc-400",
};

function ReingestControl({ body, year, onDone }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("full");
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);
  const enabled = !!(body && year);

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const poll = (jobId) => {
    getDiaryReingestStatus(jobId).then(({ data }) => {
      setJob(data);
      if (data.status === "completed" || data.status === "failed") {
        setRunning(false);
        if (data.status === "completed") { toast.success(`Re-ingest done: ${body} ${year} — ${data.processed}/${data.total} files, ${data.errors || 0} errors`); onDone?.(); }
        else toast.error(`Re-ingest failed: ${data.error || "unknown"}`);
        return;
      }
      pollRef.current = setTimeout(() => poll(jobId), 3000);
    }).catch(() => { pollRef.current = setTimeout(() => poll(jobId), 5000); });
  };

  const start = async () => {
    const label = SCOPES.find((s) => s[0] === scope)?.[1];
    if (!window.confirm(`CLEAN re-ingest ${body} ${year} — ${label}?\n\nThis backs up, then CLEARS and re-ingests the ${scope === "sm" ? "seat matrices" : scope === "allot" ? "allotments" : "allotments + seat matrices"} for ${body} ${year}. A backup is taken first (older backups are dropped on success).`)) return;
    setRunning(true); setJob(null);
    try {
      const { data } = await startDiaryReingest(body, parseInt(year), scope);
      toast.success(`Started re-ingest: ${body} ${year}`);
      poll(data.job_id);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || "Could not start re-ingest");
    }
  };

  return (
    <div className="relative ml-auto">
      <button onClick={() => enabled && setOpen((o) => !o)} disabled={!enabled} data-testid="diary-reingest-toggle"
        title={enabled ? "Clean re-ingest this state-year" : "Pick a Body and Year to enable re-ingest"}
        className={`h-7 px-3 flex items-center gap-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-colors ${enabled ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600" : "bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed"}`}>
        <ArrowClockwise size={12} weight="bold" className={running ? "animate-spin" : ""} /> Re-ingest ▸
      </button>

      {open && enabled && (
        <div className="absolute right-0 top-9 z-30 w-[360px] bg-white rounded-xl border border-zinc-200 shadow-2xl p-3" data-testid="diary-reingest-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#09090B]">Re-ingest {body} · {year}</span>
            <button onClick={() => setOpen(false)} data-testid="diary-reingest-close" className="h-6 w-6 flex items-center justify-center rounded hover:bg-zinc-100"><X size={13} weight="bold" /></button>
          </div>
          <p className="text-[10px] text-zinc-500 leading-relaxed mb-2">
            Clean re-ingest from the diary's Firebase sources — backs up, clears, then replays each PDF in order through the state parser (derived data + caches rebuild automatically). One-off state fixes (e.g. AP stray-round relabel) are NOT applied here — run those from the Migrations panel.
          </p>
          <div className="flex items-center gap-1.5 mb-2">
            {SCOPES.map(([val, lbl]) => (
              <button key={val} onClick={() => setScope(val)} disabled={running} data-testid={`diary-reingest-scope-${val}`}
                className={`h-7 px-2 rounded-lg text-[9px] font-bold uppercase tracking-wider border ${scope === val ? "bg-[#09090B] text-white border-[#09090B]" : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50"}`}>
                {val === "full" ? "Full" : val === "allot" ? "Allotments" : "Seat Matrix"}
              </button>
            ))}
          </div>
          <button onClick={start} disabled={running} data-testid="diary-reingest-start"
            className="w-full h-8 rounded-lg bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-1.5">
            {running ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <ArrowClockwise size={12} weight="bold" />}
            {running ? "Running…" : "Backup + Clean re-ingest"}
          </button>

          {job && (
            <div className="mt-3 border-t border-zinc-100 pt-2" data-testid="diary-reingest-progress">
              <div className="flex items-center gap-1.5 text-[10px] font-mono text-[#6043EF] mb-1">
                {job.status === "processing" && <span className="w-2 h-2 rounded-full bg-[#6043EF] animate-pulse" />}
                <span>{job.stage}{job.total ? ` · ${job.processed || 0}/${job.total}` : ""}</span>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-0.5">
                {(job.files || []).map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px]" data-testid={`diary-reingest-file-${i}`}>
                    <span className={`font-bold ${FILE_STATUS_CLS[f.status] || "text-zinc-400"}`}>
                      {f.status === "completed" ? "✓" : f.status === "error" ? "✕" : f.status === "processing" ? "▸" : "·"}
                    </span>
                    <span className="text-zinc-500">{f.type === "allotment" ? `R${f.round}` : "SM"}</span>
                    <span className="text-zinc-700 truncate flex-1" title={f.name}>{f.name}</span>
                    {f.status === "completed" && <span className="text-zinc-400 tabular-nums">{f.records}r</span>}
                    {f.status === "error" && <span className="text-rose-500 truncate max-w-[100px]" title={f.note}>{f.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function fmtRel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

// Absolute date-time, e.g. "07 Aug 2026, 06:22" — shown on rows/cards.
function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ReingestStamp({ e }) {
  const ok = e.reingest_last_success_at;
  const err = e.reingest_last_error_at;
  const errNewest = err && (!ok || err > ok);
  if (errNewest) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-rose-600 shrink-0"
        title={`Last re-ingest failed ${new Date(err).toLocaleString()}${e.reingest_last_error ? `\n${e.reingest_last_error}` : ""}`}
        data-testid={`diary-reingest-stamp-${e.ingest_id}`}>
        <ArrowClockwise size={9} weight="bold" /> failed {fmtRel(err)}
      </span>
    );
  }
  if (ok) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 shrink-0"
        title={`Last re-ingested OK ${new Date(ok).toLocaleString()}`}
        data-testid={`diary-reingest-stamp-${e.ingest_id}`}>
        <ArrowClockwise size={9} weight="bold" /> {fmtRel(ok)}
      </span>
    );
  }
  return null;
}

function ProgressLine({ prog, ingestId, onCancel }) {
  const determinate = (prog.total_pages || 0) > 0;
  const pct = determinate ? (prog.percent || 0) : null;
  return (
    <div className="px-4 pb-2.5 pt-0.5" data-testid={`diary-row-progress-${ingestId}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF] flex items-center gap-1.5">
          <ArrowClockwise size={11} weight="bold" className="animate-spin" /> Re-ingesting…
        </span>
        <div className="flex items-center gap-2">
          {determinate && <span className="text-[10px] font-bold text-[#6043EF] tabular-nums">{pct}%</span>}
          {prog.job_id && (
            <button onClick={() => onCancel(prog.job_id)} title="Cancel re-ingest" data-testid={`diary-cancel-${ingestId}`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-500 hover:text-rose-700">
              <X size={11} weight="bold" /> Cancel
            </button>
          )}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-violet-100 overflow-hidden">
        {determinate
          ? <div className="h-full bg-[#6043EF] transition-all duration-500" style={{ width: `${pct}%` }} />
          : <div className="h-full w-1/3 bg-[#6043EF] animate-pulse" />}
      </div>
      <div className="mt-1 flex items-center gap-4 text-[10px] text-zinc-500">
        {determinate && <span>Pages: <b className="text-zinc-800 tabular-nums">{prog.pages || 0}/{prog.total_pages}</b></span>}
        <span>Records parsed: <b className="text-zinc-800 tabular-nums">{(prog.records || 0).toLocaleString()}</b></span>
      </div>
    </div>
  );
}


export default function IngestionDiary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [range, setRange] = useState("180");
  const [filters, setFilters] = useState({ doc_type: "", body: "", exam: "", stream: "", year: "" });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [feesSession, setFeesSession] = useState(null); // { id, body, filename, docUrl } for the Fees-tool popup
  const [reconEntry, setReconEntry] = useState(null); // { ingestId, filename } → re-open the columns recon viewer
  const openAllotReconcile = async (d) => {
    try {
      const { data } = await allotmentReconOpen({ counselling_body: d.counselling_body, year: d.year, round_number: d.round_number, exam: d.exam });
      setReconEntry({ ingestId: data.ingest_id, filename: `${d.body_display || d.counselling_body} · ${d.year} · R${d.round_number} — reconcile` });
      setDetail(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open reconcile"); }
  };
  const openSMReconcile = async (d) => {
    try {
      // NMC seat matrix reconciles through the same overlay via source_tag="nmc_sm"
      // (simple intake payload → apply writes nmc_data.seats, not seat_matrix_detail).
      const isNmc = d.doc_type === "nmc_seat_matrix" || d.counselling_body === "NMC";
      const { data } = await smReviewOpen({ body: d.counselling_body, year: d.year,
        round_number: d.round_number, ...(d.file_hash ? { file_hash: d.file_hash } : {}),
        ...(isNmc ? { source_tag: "nmc_sm" } : {}) });
      const bodyLabel = d.body_display || d.counselling_body;
      // SM round scheme (50+/100+/200+, see server._SM_LOG_MAP): 50=Full,
      // 51-59=Prov R1-9, 150=Stray, 250=Sp.Stray. NMC reuses round as a
      // publication version (no counselling rounds), so label it as such.
      const rn = d.round_number;
      let rlabel = "";
      if (isNmc) rlabel = rn ? ` · pub v${rn}` : "";
      else if (rn == null) rlabel = "";
      else if (rn === 50) rlabel = " · Full";
      else if (rn >= 51 && rn <= 59) rlabel = ` · Prov R${rn - 50}`;
      else if (rn === 150) rlabel = " · Stray";
      else if (rn === 250) rlabel = " · Sp.Stray";
      else rlabel = ` · R${rn}`;
      setReconEntry({ ingestId: data.ingest_id, filename: `${bodyLabel} Seat-Matrix · ${d.year}${rlabel} — reconcile colleges` });
      setDetail(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open SM reconcile"); }
  };
  const [feesPdfOpen, setFeesPdfOpen] = useState(false);
  const [feesPdfPct, setFeesPdfPct] = useState(30);
  const [feesDragging, setFeesDragging] = useState(false);
  const feesBodyRef = useRef(null);
  const startPdfDrag = (e) => {
    e.preventDefault();
    setFeesDragging(true);
    const move = (ev) => {
      const el = feesBodyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const pct = ((rect.right - ev.clientX) / rect.width) * 100;
      setFeesPdfPct(Math.min(70, Math.max(15, pct)));
    };
    const up = () => { setFeesDragging(false); window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  // Calendar state: mode (week|month|day), anchor date, and selected day (day mode).
  const [calMode, setCalMode] = useState("month");
  const [calAnchor, setCalAnchor] = useState(new Date());
  const [calDay, setCalDay] = useState(null);
  const [showBackfill, setShowBackfill] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), ...presetRange(range) };
    Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
    getIngestions(params)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load ingestions"))
      .finally(() => setLoading(false));
  }, [filters, range]);
  useEffect(() => { load(); }, [load]);

  // Poll active re-ingest jobs so progress can render INLINE on the running
  // diary row (files run one at a time — file-mode and state-year alike).
  const [reingestJobs, setReingestJobs] = useState([]);
  const prevJobStatus = useRef({});
  const rjPollRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const DONE = new Set(["completed", "failed", "interrupted", "cancelled", "stalled"]);
    const tick = () => {
      listDiaryReingest().then(({ data }) => {
        if (!alive) return;
        const js = data.jobs || [];
        js.forEach((j) => {
          if (prevJobStatus.current[j.job_id] === "processing" && DONE.has(j.status)) {
            load();
            if (j.status === "completed") toast.success(`Re-ingest done: ${j.body} ${j.year} — ${j.processed}/${j.total} file${j.total === 1 ? "" : "s"}, ${j.errors || 0} error${(j.errors || 0) === 1 ? "" : "s"}`);
            else if (j.status === "failed") toast.error(`Re-ingest failed: ${j.body} ${j.year} — ${j.error || "unknown"}`);
          }
          prevJobStatus.current[j.job_id] = j.status;
        });
        setReingestJobs(js);
        const anyRunning = js.some((j) => j.status === "processing");
        rjPollRef.current = setTimeout(tick, anyRunning ? 2000 : 6000);
      }).catch(() => { rjPollRef.current = setTimeout(tick, 9000); });
    };
    tick();
    return () => { alive = false; clearTimeout(rjPollRef.current); };
  }, [load]);

  // Map each diary row (by ingest_id) to its live file progress. reingestJobs is
  // newest-first; a 'processing' file wins over an older completed entry.
  const progressByIngest = useMemo(() => {
    const m = {};
    for (const j of reingestJobs) {
      for (const f of (j.files || [])) {
        const id = f.ingest_id;
        if (!id) continue;
        const cur = m[id];
        if (!cur) m[id] = { ...f, jobStatus: j.status, job_id: j.job_id };
        else if (cur.status !== "processing" && f.status === "processing") m[id] = { ...f, jobStatus: j.status, job_id: j.job_id };
      }
    }
    return m;
  }, [reingestJobs]);
  const activeJob = reingestJobs.find((j) => j.status === "processing");

  const cancelJob = useCallback(async (jobId) => {
    if (!jobId || !window.confirm("Cancel this re-ingest?\n\nThe currently-parsing file can't be stopped mid-way, but no further files will run and you can restart immediately. Already-finished files are kept (re-ingest is idempotent).")) return;
    try {
      await cancelDiaryReingest(jobId);
      toast.success("Re-ingest cancelled");
      // Optimistically flip the job + its in-flight file so the inline progress
      // bar clears instantly (the poller confirms within a few seconds).
      setReingestJobs((js) => js.map((j) => j.job_id === jobId
        ? { ...j, status: "cancelled", files: (j.files || []).map((f) => f.status === "processing" ? { ...f, status: "cancelled" } : f) }
        : j));
      prevJobStatus.current[jobId] = "cancelled";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => { setFilters({ doc_type: "", body: "", exam: "", stream: "", year: "" }); setRange("180"); };
  const activeCount = Object.values(filters).filter(Boolean).length + (range !== "180" ? 1 : 0);

  const openDetail = (ingestId) => {
    setDetail({ ingest_id: ingestId }); setDetailLoading(true);
    getIngestionDetail(ingestId)
      .then((r) => setDetail(r.data))
      .catch(() => { toast.error("Failed to load detail"); setDetail(null); })
      .finally(() => setDetailLoading(false));
  };

  const reinvokeFees = async (ingestId) => {
    const t = toast.loading("Opening the Fees tool for this document…");
    try {
      const { data } = await diaryIngestFees(ingestId);
      toast.dismiss(t);
      if (data?.ok && data.session_id) {
        setFeesSession({ id: data.session_id, body: data.body, filename: data.filename, docUrl: data.doc_url });
      } else {
        toast.error(data?.error || "Could not open the Fees tool for this row.");
      }
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.error || e?.response?.data?.detail || "Could not open the Fees tool.");
    }
  };

  const openDoc = async (e) => {
    if (e.source_url) { window.open(e.source_url, "_blank"); return; }
    if (e.source_key) {
      const t = toast.loading("Fetching document…");
      try { const r = await getSignedDocUrl(e.source_key); window.open(r.data.url, "_blank"); toast.dismiss(t); }
      catch { toast.error("No document link available", { id: t }); }
      return;
    }
    // Uploaded ingests (e.g. NMC) stash the PDF in object storage, served here.
    if (e.has_pdf && e.ingest_id) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/ingest/${e.ingest_id}/pdf`, "_blank");
      return;
    }
    toast.info("No stored document — bulk uploads need a re-upload backfill to link.");
  };

  const entries = data?.entries || [];
  const facets = data?.facets || {};

  const byDate = useMemo(() => {
    const m = {};
    for (const e of entries) { (m[e.date] = m[e.date] || []).push(e); }
    return m;
  }, [entries]);
  const dates = Object.keys(byDate).sort().reverse();

  // Default calendar mode follows the range preset; anchor jumps to latest data.
  useEffect(() => {
    if (view !== "calendar") return;
    setCalMode(range === "7" ? "week" : "month");
    setCalDay(null);
    if (dates.length) setCalAnchor(new Date(dates[0] + "T00:00:00"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, range, data]);

  return (
    <div className="border-2 border-zinc-200 rounded-xl bg-white mb-5 overflow-hidden" data-testid="ingestion-diary">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center gap-3 flex-wrap">
        <Package size={16} weight="bold" className="text-[#6043EF]" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-[#09090B]">Ingestion Diary</span>
        <span className="text-[11px] text-zinc-500">{data?.total ?? 0} ingestion{(data?.total ?? 0) === 1 ? "" : "s"}</span>
        {activeJob && (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider" data-testid="diary-active-reingest-chip">
            <ArrowClockwise size={11} weight="bold" className="animate-spin" />
            Re-ingesting {activeJob.body} {activeJob.year} · {activeJob.stage}{activeJob.total > 1 ? ` · ${activeJob.processed || 0}/${activeJob.total}` : ""}
            <button onClick={() => cancelJob(activeJob.job_id)} title="Cancel re-ingest" data-testid="diary-cancel-active-chip"
              className="ml-0.5 h-3.5 w-3.5 flex items-center justify-center rounded-full hover:bg-amber-200">
              <X size={9} weight="bold" />
            </button>
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setShowBackfill(true)} data-testid="diary-backfill-open"
            className="h-7 px-2.5 flex items-center gap-1 rounded-lg bg-white border border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-600 hover:border-[#6043EF] hover:text-[#6043EF]">
            <MagnifyingGlass size={12} weight="bold" /> Detect &amp; Backfill
          </button>
          <button onClick={load} title="Refresh" data-testid="diary-refresh" className="h-7 w-7 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-200">
            <ArrowClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
          <div className="flex rounded-lg overflow-hidden border border-zinc-200">
            <button onClick={() => setView("list")} data-testid="diary-view-list"
              className={`h-7 px-2.5 flex items-center gap-1 text-[10px] font-bold uppercase ${view === "list" ? "bg-[#6043EF] text-white" : "bg-white text-zinc-500"}`}>
              <Rows size={12} weight="bold" /> List
            </button>
            <button onClick={() => setView("calendar")} data-testid="diary-view-calendar"
              className={`h-7 px-2.5 flex items-center gap-1 text-[10px] font-bold uppercase border-l border-zinc-200 ${view === "calendar" ? "bg-[#6043EF] text-white" : "bg-white text-zinc-500"}`}>
              <CalendarBlank size={12} weight="bold" /> Calendar
            </button>
          </div>
        </div>
      </div>

      {/* Date range presets */}
      <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2 flex-wrap bg-white">
        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Range</span>
        <div className="flex rounded-lg overflow-hidden border border-zinc-200" data-testid="diary-range-presets">
          {RANGES.map(([val, lbl]) => (
            <button key={val} onClick={() => setRange(val)} data-testid={`diary-range-${val}`}
              className={`h-7 px-3 text-[10px] font-bold uppercase tracking-wider transition-colors ${range === val ? "bg-[#09090B] text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}>
              {lbl}
            </button>
          ))}
        </div>
        {activeCount > 0 && (
          <button onClick={clearFilters} data-testid="diary-clear-filters"
            className="ml-auto h-7 px-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-rose-600 hover:bg-rose-50">
            Reset ({activeCount})
          </button>
        )}
      </div>

      {/* Facet filter bar */}
      <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center gap-2 flex-wrap bg-white">
        <FunnelSimple size={13} weight="bold" className="text-zinc-400" />
        <Sel testid="diary-filter-doctype" value={filters.doc_type} onChange={(v) => setF("doc_type", v)}
          placeholder="Type: all" options={(facets.doc_types || []).map((d) => [d, (data?.doc_type_labels?.[d]) || d])} />
        <Sel testid="diary-filter-body" value={filters.body} onChange={(v) => setF("body", v)}
          placeholder="Body: all" options={(facets.bodies || []).map((b) => [b, b])} />
        <Sel testid="diary-filter-exam" value={filters.exam} onChange={(v) => setF("exam", v)}
          placeholder="Exam: all" options={(facets.exams || []).map((x) => [x, EXAM_LABELS[x] || x])} />
        <Sel testid="diary-filter-stream" value={filters.stream} onChange={(v) => setF("stream", v)}
          placeholder="Stream: all" options={(facets.streams || []).map((s) => [s, s])} />
        <Sel testid="diary-filter-year" value={filters.year} onChange={(v) => setF("year", v)}
          placeholder="Year: all" options={(facets.years || []).map((y) => [String(y), String(y)])} />
        <ReingestControl body={filters.body} year={filters.year} onDone={load} />
      </div>

      {/* Body */}
      <div className={view === "calendar" ? "" : "max-h-[520px] overflow-y-auto"}>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-400">Loading…</div>
        ) : view === "calendar" ? (
          <Calendar mode={calMode} setMode={setCalMode} anchor={calAnchor} setAnchor={setCalAnchor}
            day={calDay} setDay={setCalDay} byDate={byDate} onOpen={openDetail} onDoc={openDoc} />
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-400" data-testid="diary-empty">No ingestions in this range.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {dates.map((d) => (
              <div key={d} data-testid={`diary-date-group-${d}`}>
                <div className="px-4 py-1.5 bg-zinc-50 sticky top-0 z-10 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  {fmtDate(d)}
                </div>
                {byDate[d].map((e) => (
                  <DiaryRow key={e.ingest_id} e={e} prog={progressByIngest[e.ingest_id]} onCancel={cancelJob} onOpen={() => openDetail(e.ingest_id)} onDoc={() => openDoc(e)} onDeleted={load} onReinvokeFees={reinvokeFees} onReconcile={(row) => setReconEntry({ ingestId: row.ingest_id, filename: row.file_name })} onReconcileAllot={openAllotReconcile} onReconcileSM={openSMReconcile} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {reconEntry && (
        <IngestReconViewer ingestId={reconEntry.ingestId} filename={reconEntry.filename}
          onClose={() => { setReconEntry(null); load(); }} />
      )}
      {detail && <DetailDrawer detail={detail} loading={detailLoading} onClose={() => setDetail(null)} onDoc={openDoc} onReload={() => openDetail(detail.ingest_id)}
        onReconcile={(d) => { setReconEntry({ ingestId: d.ingest_id, filename: d.file_name }); setDetail(null); }}
        onReconcileAllot={openAllotReconcile} onReconcileSM={openSMReconcile} />}
      {showBackfill && <BackfillModal onClose={() => setShowBackfill(false)} onLinked={load} />}
      {feesSession && (
        <div className="fixed inset-0 z-50 flex bg-black/60" data-testid="diary-fees-tool-modal" onClick={() => setFeesSession(null)}>
          <div className="bg-white w-full h-full flex flex-col overflow-hidden" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100 shrink-0">
              <div className="flex items-center gap-2">
                <MagicWand size={16} weight="fill" className="text-[#6043EF]" />
                <span className="text-sm font-black uppercase tracking-wider text-[#09090B]">Fees Tool</span>
                <span className="text-xs text-zinc-500 truncate max-w-[360px]">{feesSession.body} · {feesSession.filename}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {feesSession.docUrl && (
                  <button onClick={() => setFeesPdfOpen((v) => !v)} data-testid="diary-fees-toggle-pdf"
                    className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${feesPdfOpen ? "bg-[#6043EF] text-white" : "bg-white text-[#6043EF] border border-[#6043EF]/30 hover:bg-violet-50"}`}>
                    <FilePdf size={13} weight="bold" /> {feesPdfOpen ? "Hide PDF" : "Show PDF"}
                  </button>
                )}
                <button onClick={() => setFeesSession(null)} data-testid="diary-fees-tool-close" className="h-7 w-7 flex items-center justify-center rounded hover:bg-zinc-100"><X size={16} weight="bold" /></button>
              </div>
            </div>
            <div className="flex-1 min-h-0 flex" ref={feesBodyRef}>
              <div className="flex-1 min-w-0 min-h-0">
                <AgentChat initialSessionId={feesSession.id} hideSessions feesMode />
              </div>
              {feesSession.docUrl && feesPdfOpen && (
                <>
                  <div onMouseDown={startPdfDrag} data-testid="diary-fees-split-divider"
                    className="w-1.5 shrink-0 cursor-col-resize bg-zinc-200 hover:bg-[#6043EF]/50 transition-colors" title="Drag to resize" />
                  <div className="shrink-0 border-l border-zinc-200 bg-zinc-50 flex flex-col" style={{ width: `${feesPdfPct}%` }} data-testid="diary-fees-pdf-split">
                    <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-200 bg-white shrink-0">
                      <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Source PDF</span>
                      <a href={feesSession.docUrl} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-[#6043EF] hover:underline inline-flex items-center gap-1">Open <ArrowSquareOut size={11} weight="bold" /></a>
                    </div>
                    <iframe src={feesSession.docUrl} title="Source PDF" className={`flex-1 w-full ${feesDragging ? "pointer-events-none" : ""}`} data-testid="diary-fees-pdf-frame" />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BackfillModal({ onClose, onLinked }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (apply) => {
    if (!files.length) { toast.info("Choose PDF file(s) first"); return; }
    setBusy(true);
    try {
      const r = await detectBackfillIngestions(files, apply);
      setRes(r.data);
      if (apply && r.data.linked > 0) { toast.success(`Linked ${r.data.linked} ingestion(s) to Firebase`); onLinked(); }
      else if (apply) toast.info("Nothing new to link");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Detection failed");
    } finally { setBusy(false); }
  };

  const ACTION_META = {
    linked: ["bg-emerald-100 text-emerald-700", "Linked"],
    match_found: ["bg-blue-100 text-blue-700", "Match found"],
    already_linked: ["bg-zinc-100 text-zinc-500", "Already linked"],
    no_match: ["bg-amber-100 text-amber-700", "No match"],
  };
  const linkable = res ? res.results.filter((r) => r.action === "match_found").length : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="diary-backfill-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MagnifyingGlass size={16} weight="bold" className="text-[#6043EF]" />
            <span className="text-[12px] font-bold uppercase tracking-wider text-[#09090B]">Detect &amp; Backfill Documents</span>
          </div>
          <button onClick={onClose} data-testid="diary-backfill-close" className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-zinc-100"><X size={15} weight="bold" /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
            Drop the original <b>PDF / XLSX files or a ZIP</b> (ZIPs are expanded like bulk ingestion). Each file is auto-detected (body · year · type) using the bulk-ingestion classifier and matched to past ingestions by <b>sha256 hash</b> (exact) or <b>filename + body + year</b>. Matched files are stored to Firebase and linked to their ingestion.
          </p>

          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-200 rounded-xl py-6 cursor-pointer hover:border-[#6043EF] transition-colors" data-testid="diary-backfill-dropzone">
            <UploadSimple size={22} weight="bold" className="text-zinc-400" />
            <span className="text-[11px] text-zinc-500">{files.length ? `${files.length} file(s) selected` : "Click to choose PDF / XLSX / ZIP files"}</span>
            <input type="file" multiple accept=".pdf,.xlsx,.xls,.zip" className="hidden" data-testid="diary-backfill-input"
              onChange={(e) => { setFiles(Array.from(e.target.files || [])); setRes(null); }} />
          </label>

          <div className="flex items-center gap-2 mt-3">
            <button onClick={() => run(false)} disabled={busy || !files.length} data-testid="diary-backfill-detect"
              className="h-8 px-3 rounded-lg bg-zinc-900 text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5">
              {busy ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <MagnifyingGlass size={12} weight="bold" />} Detect
            </button>
            {res && linkable > 0 && (
              <button onClick={() => run(true)} disabled={busy} data-testid="diary-backfill-apply"
                className="h-8 px-3 rounded-lg bg-[#6043EF] text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5">
                <LinkSimple size={12} weight="bold" /> Link {linkable} to Firebase
              </button>
            )}
          </div>

          {res && (
            <div className="mt-4 space-y-2" data-testid="diary-backfill-results">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                {res.files} file(s) · {res.matched} matched · {res.linked} linked
              </div>
              {res.results.map((r, i) => {
                const [cls, lbl] = ACTION_META[r.action] || ACTION_META.no_match;
                const d = r.detected;
                return (
                  <div key={i} className="border border-zinc-200 rounded-lg p-2.5" data-testid={`diary-backfill-row-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.action === "linked" || r.action === "already_linked"
                        ? <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                        : r.action === "no_match" ? <WarningCircle size={14} weight="fill" className="text-amber-500" /> : null}
                      <span className="text-[12px] font-bold text-[#09090B] truncate max-w-[240px]" title={r.filename}>{r.filename}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${cls}`}>{lbl}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      Detected: <b>{d.body_display || "?"}</b> · {d.year || "?"} · {d.doc_type_label}{d.round_number != null ? ` · R${d.round_number}` : ""} · <span className="text-zinc-400">{d.confidence}</span>
                    </div>
                    {r.matches?.length > 0 && (
                      <div className="text-[10px] text-zinc-400 mt-1">
                        → {r.matches.length} ingestion(s) [{r.matches.map((m) => `${m.match_by}:${String(m.ingest_id).slice(0, 6)}`).join(", ")}]
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const REINGESTABLE_DOCS = new Set(["allotment", "full_seat_matrix", "provisional_seat_matrix", "stray_seat_matrix", "nmc_seat_matrix"]);
const SM_DOC_TYPES = new Set(["full_seat_matrix", "provisional_seat_matrix", "stray_seat_matrix", "nmc_seat_matrix"]);

function DiaryRow({ e, prog, onCancel, onOpen, onDoc, onDeleted, onReinvokeFees, onReconcile, onReconcileAllot, onReconcileSM }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [cleanup, setCleanup] = useState(false);
  const isMerit = e.doc_type === "merit_list";
  const canReingest = e.source_key && REINGESTABLE_DOCS.has(e.doc_type);
  const canReinvokeFees = e.doc_type === "fees";
  const canReconcile = /^(feescsv_|agentcols_)/.test(String(e.ingest_id || ""));
  const canReconcileAllot = (e.log_type === "Allotment" || (e.doc_type || "").includes("allot")) && !!e.counselling_body && e.round_number != null;
  const canReconcileSM = (e.log_type === "Seat Matrix" || SM_DOC_TYPES.has(e.doc_type)) && !!e.counselling_body && !!e.year;
  const canCleanup = SM_DOC_TYPES.has(e.doc_type) && e.year;
  const isProcessing = prog?.status === "processing" && prog?.jobStatus === "processing";
  const isError = prog?.status === "error";
  const reingest = async () => {
    if (!window.confirm(`Re-ingest this document?\n\n${e.body_display} · ${e.year || ""}${e.round_number != null ? ` · R${e.round_number}` : ""} — ${e.doc_type_label}\n\nRefreshes just this file (upsert). For a full clean rebuild use the state-year "Re-ingest" in the filter bar. Live progress shows on this row.`)) return;
    setBusy(true);
    try {
      await startDiaryReingestFile(e.ingest_id);
      toast.success("Re-ingest started — progress shows on this row");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start re-ingest");
    } finally { setBusy(false); }
  };
  return (
    <div className={`hover:bg-zinc-50 group ${isProcessing ? "bg-violet-50/40" : ""}`} data-testid={`diary-row-${e.ingest_id}`}>
      <div className="px-4 py-2 flex items-center gap-2.5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-[13px] font-bold text-[#09090B] shrink-0">{e.body_display}</span>
          <Chip cls="bg-blue-50 text-blue-700">{EXAM_LABELS[e.exam] || e.exam}</Chip>
          <Chip cls={DOC_TYPE_COLORS[e.doc_type] || "bg-zinc-100 text-zinc-500"}>{e.doc_type_label}</Chip>
          {e.year && <span className="text-[11px] text-zinc-400">{e.year}{(e.round_label != null || e.round_number != null) ? ` · ${e.counselling_body === "NMC" ? "D" : "R"}${e.round_label ?? e.round_number}` : ""}</span>}
          {e.publication_date && <span className="text-[10px] font-medium text-emerald-600 shrink-0" title="Publication date on the document" data-testid={`diary-pubdate-${e.ingest_id}`}>pub {e.publication_date}</span>}
          <span className="text-[11px] truncate hidden sm:inline-flex items-center gap-1" title={e.file_name}>
            <span className="text-zinc-400" aria-hidden="true">·</span>
            <FileTypeIcon format={e.source_format} />
            {(e.has_pdf || e.source_url || e.source_key) ? (
              <button type="button" onClick={onDoc}
                className={`truncate font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid ${fileFmtColor(e.source_format)}`}
                title="Open source PDF" data-testid={`diary-filename-${e.ingest_id}`}>{e.file_name}</button>
            ) : (
              <span className={`truncate font-medium ${fileFmtColor(e.source_format)}`} data-testid={`diary-filename-${e.ingest_id}`}>{e.file_name}</span>
            )}
          </span>
          {e.ingested_at && (
            <span className="text-[10px] text-zinc-400 shrink-0 hidden md:inline-flex items-center gap-0.5" title="Crawled / ingested at" data-testid={`diary-time-${e.ingest_id}`}>
              <ClockCounterClockwise size={10} weight="bold" /> {fmtDateTime(e.ingested_at)}
            </span>
          )}
        </div>
        <ReingestStamp e={e} />
        {isError
          ? <span className="text-[10px] font-bold text-rose-600 shrink-0 truncate max-w-[160px]" title={prog.note}>failed: {prog.note || "error"}</span>
          : <span className="text-[10px] text-zinc-400 shrink-0">{e.records || 0} rows · {e.colleges || 0} clg</span>}
        {canReinvokeFees && (
          <button onClick={() => onReinvokeFees(e.ingest_id)} disabled={busy} title="Re-invoke the agentic Fees tool for this document" data-testid={`diary-reinvoke-fees-${e.ingest_id}`}
            className="h-6 w-6 flex items-center justify-center rounded text-zinc-400 hover:text-[#6043EF] hover:bg-violet-50 disabled:opacity-50">
            <MagicWand size={13} weight="fill" />
          </button>
        )}
        {canReingest && (
          <button onClick={reingest} disabled={busy || isProcessing} title="Re-ingest this document (refresh this round / seat matrix)" data-testid={`diary-reingest-file-${e.ingest_id}`}
            className="h-6 w-6 flex items-center justify-center rounded text-zinc-400 hover:text-amber-600 hover:bg-amber-50 disabled:opacity-50">
            <ArrowClockwise size={13} weight="bold" className={(busy || isProcessing) ? "animate-spin" : ""} />
          </button>
        )}
        {canCleanup && (
          <button onClick={() => setCleanup(true)} title="Clean up seat-matrix rows for this round/type (legacy fix)" data-testid={`diary-sm-cleanup-${e.ingest_id}`}
            className="h-6 w-6 flex items-center justify-center rounded text-zinc-400 hover:text-rose-600 hover:bg-rose-50">
            <Trash size={13} weight="bold" />
          </button>
        )}
        {canReconcile && (
          <button onClick={() => onReconcile(e)} title="Open the reconciliation table again (review / re-apply columns — no re-ingest needed)" data-testid={`diary-reconcile-${e.ingest_id}`}
            className="h-6 px-2 flex items-center gap-1 rounded text-[13px] font-semibold text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50">
            <ArrowClockwise size={15} weight="bold" /> Reconcile
          </button>
        )}
        {canReconcileAllot && (
          <button onClick={() => onReconcileAllot(e)} title="Reconcile the unmatched colleges for this allotment round (LLM match, create new, apply all)" data-testid={`diary-reconcile-allot-${e.ingest_id}`}
            className="h-6 px-2 flex items-center gap-1 rounded text-[13px] font-semibold text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50">
            <ArrowClockwise size={15} weight="bold" /> Reconcile
          </button>
        )}
        {canReconcileSM && (
          <button onClick={() => onReconcileSM(e)} title="Reconcile this seat-matrix ingest — see every matched college (re-map any wrongly matched one) and resolve/create the unmatched ones" data-testid={`diary-reconcile-sm-${e.ingest_id}`}
            className="h-6 px-2 flex items-center gap-1 rounded text-[13px] font-semibold text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50">
            <ArrowClockwise size={15} weight="bold" /> Reconcile
          </button>
        )}
        {isMerit && (
          <button
            onClick={() => navigate(`/admin/merit-list?body=${encodeURIComponent(e.counselling_body || "")}&year=${e.year || ""}`)}
            title="View the ingested merit / rank list (true NEET AIR by application no.)"
            data-testid={`diary-view-merit-${e.ingest_id}`}
            className="h-6 px-2 flex items-center gap-1 rounded text-[13px] font-semibold text-zinc-500 hover:text-pink-600 hover:bg-pink-50">
            <Trophy size={15} weight="bold" /> Merit list
          </button>
        )}
        {(e.source_key || e.source_url) && (
          <button onClick={onDoc} title="Open source document" data-testid={`diary-doc-${e.ingest_id}`}
            className="h-6 w-6 flex items-center justify-center rounded text-[#6043EF] hover:bg-violet-50">
            <ArrowSquareOut size={19} weight="bold" />
          </button>
        )}
        <button onClick={onOpen} title="View ingestion details" data-testid={`diary-open-${e.ingest_id}`}
          className="h-6 px-2 flex items-center gap-1 rounded text-[10px] font-mono text-zinc-500 hover:text-[#6043EF] hover:bg-violet-50">
          <LinkSimple size={12} weight="bold" /> {String(e.ingest_id).slice(0, 8)}
        </button>
      </div>
      {isProcessing && <ProgressLine prog={prog} ingestId={e.ingest_id} onCancel={onCancel} />}
      {cleanup && <SeatMatrixCleanupModal e={e} onClose={() => setCleanup(false)} onDeleted={onDeleted} />}
    </div>
  );
}

// Legacy cleanup: inspect the seat_matrix_detail rows for a diary SM document's
// body+year (grouped by round/type), then delete the selected group(s).
function SeatMatrixCleanupModal({ e, onClose, onDeleted }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const keyOf = (t) => (t === null || t === undefined ? "__null__" : String(t));
  useEffect(() => {
    let alive = true;
    inspectDiarySeatMatrix({
      counselling_body: e.counselling_body, exam: e.exam, year: e.year,
      doc_type: e.doc_type, round_number: e.round_number, matrix_type: e.matrix_type,
    }).then((r) => {
      if (!alive) return;
      const g = r.data.groups || [];
      setGroups(g);
      const initial = {};
      g.forEach((x) => { if (x.matches_doc) initial[keyOf(x.type)] = true; });
      setSel(initial);
      setLoading(false);
    }).catch(() => { if (alive) { toast.error("Failed to inspect seat-matrix rows"); setLoading(false); } });
    return () => { alive = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (t) => setSel((s) => ({ ...s, [keyOf(t)]: !s[keyOf(t)] }));
  const chosen = groups.filter((g) => sel[keyOf(g.type)]);
  const totRows = chosen.reduce((a, g) => a + g.rows, 0);
  const totClg = chosen.reduce((a, g) => a + g.colleges, 0);
  const del = async () => {
    if (!chosen.length) return;
    if (!window.confirm(`Delete ${totRows} seat-matrix row(s) across ${totClg} college(s) for ${e.body_display} ${e.year}?\n\nTypes: ${chosen.map((g) => g.type ?? "full matrix").join(", ")}\n\nThis permanently removes the rows and re-derives college intake. Cannot be undone.`)) return;
    setBusy(true);
    try {
      const types = chosen.map((g) => g.type);
      const r = await deleteDiarySeatMatrix({ counselling_body: e.counselling_body, exam: e.exam, year: e.year, types });
      toast.success(`Deleted ${r.data.deleted} row(s); re-derived ${r.data.affected_colleges?.length || 0} college(s)`);
      onDeleted?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="sm-cleanup-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2">
          <Trash size={18} weight="bold" className="text-rose-600" />
          <div className="min-w-0">
            <div className="text-sm font-bold text-[#09090B]">Clean up seat-matrix rows</div>
            <div className="text-[11px] text-zinc-500 truncate">{e.body_display} · {e.year}{e.round_number != null ? ` · R${e.round_number}` : ""} — {e.doc_type_label}</div>
          </div>
          <button onClick={onClose} className="ml-auto h-7 w-7 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100" data-testid="sm-cleanup-close"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 max-h-[50vh] overflow-y-auto">
          <p className="text-[11px] text-zinc-500 mb-3">
            Seat-matrix rows in <b>{e.body_display} {e.year}</b>, grouped by round/type. Rows this document likely created are pre-selected. Choose the group(s) to delete.
          </p>
          {loading ? (
            <div className="py-8 text-center text-xs text-zinc-400">Inspecting…</div>
          ) : groups.length === 0 ? (
            <div className="py-8 text-center text-xs text-zinc-400" data-testid="sm-cleanup-empty">No seat-matrix rows found for {e.body_display} {e.year}.</div>
          ) : (
            <div className="space-y-2">
              {groups.map((g) => {
                const k = keyOf(g.type);
                const label = g.type == null ? "Full matrix" : g.type;
                return (
                  <label key={k} data-testid={`sm-cleanup-group-${k}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${sel[k] ? "border-rose-300 bg-rose-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                    <input type="checkbox" checked={!!sel[k]} onChange={() => toggle(g.type)} className="accent-rose-600" data-testid={`sm-cleanup-check-${k}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-bold text-[#09090B] font-mono">{label}</span>
                        {g.matches_doc && <Chip cls="bg-amber-100 text-amber-700">matches this doc</Chip>}
                      </div>
                      <div className="text-[10px] text-zinc-500">{g.rows} rows · {g.colleges} colleges</div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-zinc-100 flex items-center gap-3">
          <span className="text-[11px] text-zinc-500">{chosen.length ? `${totRows} rows · ${totClg} colleges selected` : "Nothing selected"}</span>
          <button onClick={del} disabled={!chosen.length || busy} data-testid="sm-cleanup-delete"
            className="ml-auto h-9 px-4 flex items-center gap-1.5 rounded-lg bg-rose-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-rose-700 disabled:opacity-40">
            <Trash size={14} weight="bold" /> Delete rows
          </button>
        </div>
      </div>
    </div>
  );
}

// Compact card for the week matrix cells — doc_type is implied by the row.
function MatrixCard({ e, onOpen, onDoc }) {
  return (
    <div className="rounded border border-zinc-200 bg-white p-1 group" data-testid={`diary-matrix-card-${e.ingest_id}`}>
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[10px] font-bold text-[#09090B] leading-tight">{e.body_display}</span>
        <Chip cls="bg-blue-50 text-blue-700">{EXAM_LABELS[e.exam] || e.exam}</Chip>
      </div>
      <div className="text-[9px] text-zinc-400 leading-tight mt-0.5">
        {e.year || "—"}{e.round_number != null ? ` · R${e.round_number}` : ""} · {e.records || 0} rows
      </div>
      <div className="flex items-center gap-0.5 mt-0.5">
        {(e.source_key || e.source_url) && (
          <button onClick={() => onDoc(e)} title="Open source document" data-testid={`diary-matrix-doc-${e.ingest_id}`}
            className="h-5 w-5 flex items-center justify-center rounded text-zinc-400 hover:text-[#6043EF] hover:bg-violet-50">
            <FileArrowDown size={11} weight="bold" />
          </button>
        )}
        <button onClick={() => onOpen(e.ingest_id)} title="View details" data-testid={`diary-matrix-open-${e.ingest_id}`}
          className="h-5 px-1 flex items-center gap-0.5 rounded text-[8px] font-mono text-zinc-400 hover:text-[#6043EF] hover:bg-violet-50">
          <LinkSimple size={10} weight="bold" />{String(e.ingest_id).slice(0, 6)}
        </button>
      </div>
    </div>
  );
}

// Rich card used in week/day columns — more space than a month chip.
function IngestCard({ e, onOpen, onDoc }) {
  return (
    <div className={`rounded-lg border p-2 space-y-1 ${DOC_TYPE_COLORS[e.doc_type]?.replace("text-", "border-").replace("bg-", "bg-") || "border-zinc-200"} bg-white border-zinc-200`}
      data-testid={`diary-card-${e.ingest_id}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[12px] font-bold text-[#09090B]">{e.body_display}</span>
        <Chip cls="bg-blue-50 text-blue-700">{EXAM_LABELS[e.exam] || e.exam}</Chip>
      </div>
      <Chip cls={DOC_TYPE_COLORS[e.doc_type] || "bg-zinc-100 text-zinc-500"}>{e.doc_type_label}</Chip>
      <div className="text-[10px] text-zinc-500">
        {e.year || "—"}{e.round_number != null ? ` · R${e.round_number}` : ""} · {e.records || 0} rows · {e.colleges || 0} clg
      </div>
      {e.file_name && <div className="text-[9px] text-zinc-400 truncate" title={e.file_name}>{e.file_name}</div>}
      <div className="flex items-center gap-1 pt-0.5">
        {(e.source_key || e.source_url) && (
          <button onClick={() => onDoc(e)} title="Open source document" data-testid={`diary-card-doc-${e.ingest_id}`}
            className="h-6 px-1.5 flex items-center gap-1 rounded text-[9px] font-bold uppercase text-zinc-500 hover:text-[#6043EF] hover:bg-violet-50">
            <FileArrowDown size={11} weight="bold" /> Doc
          </button>
        )}
        <button onClick={() => onOpen(e.ingest_id)} title="View details" data-testid={`diary-card-open-${e.ingest_id}`}
          className="h-6 px-1.5 flex items-center gap-1 rounded text-[9px] font-mono text-zinc-500 hover:text-[#6043EF] hover:bg-violet-50">
          <LinkSimple size={11} weight="bold" /> {String(e.ingest_id).slice(0, 8)}
        </button>
      </div>
    </div>
  );
}

function Calendar({ mode, setMode, anchor, setAnchor, day, setDay, byDate, onOpen, onDoc }) {
  // ── Single-day view ──
  if (mode === "day" && day) {
    const items = byDate[day] || [];
    return (
      <div className="p-3" data-testid="diary-day-view">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => setMode("month")} data-testid="diary-day-back"
            className="h-7 px-2.5 flex items-center gap-1 rounded-lg border border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:bg-zinc-50">
            <ArrowLeft size={12} weight="bold" /> Calendar
          </button>
          <span className="text-[14px] font-black uppercase tracking-wider text-[#09090B]">{fmtDate(day)}</span>
          <span className="text-[11px] text-zinc-400">{items.length} ingestion{items.length === 1 ? "" : "s"}</span>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-400">No ingestions on this day.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {items.map((e) => <IngestCard key={e.ingest_id} e={e} onOpen={onOpen} onDoc={onDoc} />)}
          </div>
        )}
      </div>
    );
  }

  // ── Week view: matrix of doc_type (rows) × 7 days (columns) ──
  if (mode === "week") {
    const ws = startOfWeek(anchor);
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(ws); d.setDate(ws.getDate() + i); return d; });
    const label = `${days[0].toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`;
    const shift = (delta) => { const d = new Date(ws); d.setDate(ws.getDate() + delta * 7); setAnchor(d); };
    const todayKey = fmtKey(new Date());
    const dayKeys = days.map(fmtKey);

    // Entries in this week, indexed by doc_type → dayKey.
    const weekEntries = dayKeys.flatMap((k) => byDate[k] || []);
    const labelOf = {};
    weekEntries.forEach((e) => { labelOf[e.doc_type] = e.doc_type_label; });
    const DOC_ORDER = Object.keys(DOC_TYPE_COLORS);
    const rows = DOC_ORDER.filter((t) => weekEntries.some((e) => e.doc_type === t));
    const cellOf = (t, k) => (byDate[k] || []).filter((e) => e.doc_type === t);
    const gridCols = { gridTemplateColumns: `132px repeat(7, minmax(120px, 1fr))` };

    return (
      <div className="p-3" data-testid="diary-week-view">
        <CalHeader label={label} onPrev={() => shift(-1)} onNext={() => shift(1)} mode={mode} setMode={setMode} />
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-400">No ingestions this week.</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Header row: corner + weekday columns */}
              <div className="grid gap-1 mb-1" style={gridCols}>
                <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 flex items-end pb-1 pl-1">Type \ Day</div>
                {days.map((d, i) => {
                  const isToday = dayKeys[i] === todayKey;
                  return (
                    <div key={dayKeys[i]} className={`text-center rounded-lg py-1 ${isToday ? "bg-[#6043EF] text-white" : "bg-zinc-50 text-zinc-500"}`}>
                      <div className="text-[9px] font-bold uppercase tracking-wider">{WEEKDAYS[d.getDay()]}</div>
                      <div className="text-[13px] font-black">{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>
              {/* One row per document type */}
              {rows.map((t) => (
                <div key={t} className="grid gap-1 mb-1" style={gridCols} data-testid={`diary-week-typerow-${t}`}>
                  <div className={`rounded-lg px-2 py-2 text-[9px] font-bold uppercase tracking-wider flex items-center ${DOC_TYPE_COLORS[t] || "bg-zinc-100 text-zinc-500"}`}>
                    {labelOf[t] || t}
                  </div>
                  {dayKeys.map((k) => {
                    const items = cellOf(t, k);
                    return (
                      <div key={k} data-testid={`diary-week-cell-${t}-${k}`}
                        className={`rounded-lg border p-1 space-y-1 min-h-[64px] ${items.length ? "border-zinc-200 bg-white" : "border-zinc-100 bg-zinc-50/40"}`}>
                        {items.map((e) => <MatrixCard key={e.ingest_id} e={e} onOpen={onOpen} onDoc={onDoc} />)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Month grid ──
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const firstWeekday = new Date(y, m, 1).getDay();
  const daysIn = new Date(y, m + 1, 0).getDate();
  const todayKey = fmtKey(new Date());
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(d);
  const label = anchor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const shift = (delta) => setAnchor(new Date(y, m + delta, 1));

  return (
    <div className="p-3" data-testid="diary-calendar">
      <CalHeader label={label} onPrev={() => shift(-1)} onNext={() => shift(1)} mode={mode} setMode={setMode} />
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[9px] font-bold uppercase tracking-wider text-zinc-400 py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`b${i}`} className="min-h-[76px]" />;
          const key = `${y}-${pad(m + 1)}-${pad(d)}`;
          const items = byDate[key] || [];
          const isToday = key === todayKey;
          return (
            <button key={key} data-testid={`diary-cal-day-${key}`}
              onClick={() => { if (items.length) { setDay(key); setMode("day"); } }}
              disabled={!items.length}
              className={`text-left min-h-[76px] rounded-lg border p-1 flex flex-col gap-0.5 ${items.length ? "border-zinc-200 bg-white hover:border-[#6043EF] hover:shadow-sm cursor-pointer" : "border-zinc-100 bg-zinc-50/40 cursor-default"}`}>
              <div className={`text-[10px] font-bold mb-0.5 ${isToday ? "text-white bg-[#6043EF] rounded-full w-4 h-4 flex items-center justify-center" : "text-zinc-400"}`}>{d}</div>
              {items.slice(0, 3).map((e) => (
                <span key={e.ingest_id} title={`${e.body_display} · ${e.doc_type_label}${e.file_name ? ` · ${e.file_name}` : ""}`}
                  className={`px-1 py-0.5 rounded text-[8px] font-bold truncate leading-tight ${DOC_TYPE_COLORS[e.doc_type] || "bg-zinc-100 text-zinc-500"}`}>
                  {e.body_display}
                </span>
              ))}
              {items.length > 3 && <span className="text-[8px] text-zinc-400 px-1">+{items.length - 3} more</span>}
            </button>
          );
        })}
      </div>
      <p className="text-[9px] text-zinc-400 text-center mt-2">Click any day to see its ingestions in detail.</p>
    </div>
  );
}

function CalHeader({ label, onPrev, onNext, mode, setMode }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2">
        <button onClick={onPrev} data-testid="diary-cal-prev" className="h-7 w-7 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50">
          <CaretLeft size={13} weight="bold" />
        </button>
        <span className="text-[13px] font-black uppercase tracking-wider text-[#09090B] min-w-[150px] text-center" data-testid="diary-cal-label">{label}</span>
        <button onClick={onNext} data-testid="diary-cal-next" className="h-7 w-7 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50">
          <CaretRight size={13} weight="bold" />
        </button>
      </div>
      <div className="flex rounded-lg overflow-hidden border border-zinc-200">
        {[["week", "Week"], ["month", "Month"]].map(([val, lbl]) => (
          <button key={val} onClick={() => setMode(val)} data-testid={`diary-calmode-${val}`}
            className={`h-7 px-3 text-[10px] font-bold uppercase tracking-wider ${mode === val ? "bg-[#6043EF] text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}>
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

function DetailDrawer({ detail, loading, onClose, onDoc, onReload, onReconcile, onReconcileAllot }) {
  const isAllot = detail && detail.counselling_body &&
    (detail.log_type === "Allotment" || (detail.doc_type || "").includes("allot"));
  const isReconcilable = detail && /^(feescsv_|agentcols_)/.test(String(detail.ingest_id || ""));
  const widthCls = isAllot ? "max-w-[95vw]" : (detail?.fees_report && !isReconcilable) ? "max-w-3xl" : "max-w-md";
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="diary-detail-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={`relative w-full ${widthCls} bg-white h-full shadow-2xl overflow-y-auto`}>
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Ingestion Detail</span>
          <button onClick={onClose} data-testid="diary-detail-close" className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-zinc-100">
            <X size={15} weight="bold" />
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-400">Loading…</div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="text-lg font-black text-[#09090B]">{detail.body_display}</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Chip cls="bg-blue-50 text-blue-700">{EXAM_LABELS[detail.exam] || detail.exam}</Chip>
              <Chip cls={DOC_TYPE_COLORS[detail.doc_type] || "bg-zinc-100 text-zinc-500"}>{detail.doc_type_label}</Chip>
              {detail.stream && <Chip cls="bg-zinc-100 text-zinc-500">{detail.stream}</Chip>}
            </div>
            <Field label="Ingest ID" value={detail.ingest_id} mono />
            <Field label="Ingested at" value={detail.ingested_at} />
            <Field label="Year / Round" value={`${detail.year ?? "—"}${detail.round_number != null ? ` · R${detail.round_number}` : ""}`} />
            <Field label="File" value={detail.file_name} />
            <Field label="Log type" value={detail.log_type} />
            <Field label="Rows / Colleges" value={`${detail.records || 0} rows · ${detail.colleges || 0} colleges`} />
            {detail.reconciliation && (detail.reconciliation.sno_first != null || detail.reconciliation.sno_last != null) && (
              <div data-testid="diary-detail-reconciliation">
                <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1.5">
                  Reconciliation
                  {detail.reconciliation.complete === true && <span className="text-[8px] font-black uppercase bg-emerald-100 text-emerald-700 px-1 rounded">complete</span>}
                  {detail.reconciliation.complete === false && <span className="text-[8px] font-black uppercase bg-amber-100 text-amber-700 px-1 rounded">check</span>}
                </div>
                <div className="rounded-lg border border-zinc-200 overflow-hidden text-[11px]">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-zinc-50 text-[9px] font-black uppercase tracking-wider text-zinc-500">
                        <th className="text-left px-2.5 py-1.5"></th>
                        <th className="text-left px-2.5 py-1.5">Sr.No.</th>
                        <th className="text-left px-2.5 py-1.5">Rank</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      <tr>
                        <td className="px-2.5 py-1.5 font-bold text-[#09090B]">First</td>
                        <td className="px-2.5 py-1.5 font-mono tabular-nums">{detail.reconciliation.sno_first ?? "—"}</td>
                        <td className="px-2.5 py-1.5 font-mono tabular-nums">{detail.reconciliation.rank_at_first ?? "—"}</td>
                      </tr>
                      <tr>
                        <td className="px-2.5 py-1.5 font-bold text-[#09090B]">Last</td>
                        <td className="px-2.5 py-1.5 font-mono tabular-nums">{detail.reconciliation.sno_last ?? "—"}</td>
                        <td className="px-2.5 py-1.5 font-mono tabular-nums">{detail.reconciliation.rank_at_last ?? "—"}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="px-2.5 py-1.5 bg-zinc-50 text-[10px] text-zinc-600 border-t border-zinc-100">
                    Rows read <b className="text-[#09090B] font-mono">{detail.reconciliation.rows_read ?? detail.records ?? 0}</b>
                    {detail.reconciliation.sno_expected != null && <> / expected <b className="text-[#09090B] font-mono">{detail.reconciliation.sno_expected}</b> (last Sr.No.)</>}
                  </div>
                </div>
              </div>
            )}
            {detail.counselling_body && (detail.log_type === "Allotment" || (detail.doc_type || "").includes("allot")) && (
              <>
                <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-2" data-testid="diary-detail-allot-reconcile-hint">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Allotment reconciliation (new)</div>
                  <p className="text-[11px] text-zinc-600 leading-relaxed">
                    Resolve the unmatched colleges for this round in the full-screen reconciler —
                    per-state LLM match, accept-all, create new, then apply all matched at once.
                  </p>
                  <button onClick={() => onReconcileAllot(detail)} data-testid="diary-detail-reconcile-allot"
                    className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#6043EF] hover:underline">
                    <ArrowClockwise size={13} weight="bold" /> Reconcile allotment · R{detail.round_number}
                  </button>
                </div>
                <UnmatchedAllotments body={detail.counselling_body} year={detail.year}
                  round={detail.round_number} onReload={onReload} />
              </>
            )}

            {Array.isArray(detail.tables) && detail.tables.length > 0 && (
              <div data-testid="diary-detail-tables">
                <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 mb-1">
                  Tables parsed ({detail.tables.length})
                </div>
                <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
                  {detail.tables.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 px-2.5 py-1.5" data-testid={`diary-detail-table-${i}`}>
                      <span className="text-[9px] font-mono text-zinc-400 w-4 shrink-0">{i + 1}</span>
                      <span className="text-[11px] text-[#09090B] flex-1 min-w-0 truncate" title={t.section}>{t.section || `Table ${i + 1}`}</span>
                      <span className="text-[10px] font-bold text-[#6043EF] shrink-0 font-mono">{t.colleges} clg{t.rows != null ? ` · ${t.rows} rows` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detail.fees_report && (isReconcilable ? (
              <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-2" data-testid="diary-detail-reconcile-hint">
                <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Column / fees reconciliation</div>
                <p className="text-[11px] text-zinc-600 leading-relaxed">
                  Matched &amp; unmatched rows for this ingest are managed in the full-screen reconciler
                  (search &amp; assign, LLM match, apply). Open it below.
                </p>
                <button onClick={() => onReconcile(detail)} data-testid="diary-detail-reconcile"
                  className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#6043EF] hover:underline">
                  <ArrowClockwise size={13} weight="bold" /> Reconcile <span className="font-mono text-[11px] opacity-70">{detail.ingest_id}</span>
                </button>
              </div>
            ) : (
              <FeesReport report={detail.fees_report} fileName={detail.file_name} ingestId={detail.ingest_id} state={detail.state} onReload={onReload} />
            ))}
            {detail.publication_date && <Field label="Publication date" value={detail.publication_date} />}
            {(detail.source_key || detail.source_url) ? (
              <button onClick={() => onDoc(detail)} data-testid="diary-detail-doc"
                className="w-full h-9 mt-2 rounded-lg bg-[#6043EF] text-white text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 hover:bg-[#4c34c9]">
                <FileArrowDown size={13} weight="bold" /> Open source document
              </button>
            ) : (
              <div className="w-full mt-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-[10px] text-amber-800" data-testid="diary-detail-nodoc">
                No stored document. This was a bulk/manual upload parsed in-memory — re-upload the file to link it to Firebase.
              </div>
            )}
            {detail.doc_type !== "fees" && (
              <p className="text-[10px] text-zinc-400 leading-relaxed pt-1">
                Raw-row viewer (allotment/seat-matrix rows for this ingest_id) and per-ingestion revert arrive with the unified resolve/upsert cascade (next phase).
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const FEE_LABELS = {
  govt_fees: "Govt", private_fees: "Private", regular_fees: "Regular",
  other_fees: "Mgmt/Other", nri_fees: "NRI", hostel_fees: "Hostel",
  security_deposit: "Sec. Deposit", other_charges: "Other Chg.",
};
const fmtFee = (v) => (v === null || v === undefined || v === "" ? "—"
  : "₹" + Number(v).toLocaleString("en-IN"));

function downloadCsv(filename, cols, rows, withStatus) {
  const head = [...(withStatus ? ["Status"] : []), "Code", "Name", ...cols.map((c) => FEE_LABELS[c] || c)];
  const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const lines = [head.map(esc).join(",")];
  rows.forEach((r) => {
    const cells = [...(withStatus ? [r.status || ""] : []), r.code || "", r.name || "",
      ...cols.map((c) => (r[c] === null || r[c] === undefined ? "" : r[c]))];
    lines.push(cells.map(esc).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function FeesTable({ title, testid, rows, cols, withStatus, csvName, accent, assignable, ingestId, state, onReload }) {
  // only show fee columns that carry at least one value across these rows
  const visCols = cols.filter((c) => rows.some((r) => r[c] !== null && r[c] !== undefined && r[c] !== ""));
  return (
    <div data-testid={testid} className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>
          {title} <span className="text-zinc-400">({rows.length})</span>
        </div>
        {rows.length > 0 && (
          <button onClick={() => downloadCsv(csvName, visCols, rows, withStatus)}
            data-testid={`${testid}-csv`}
            className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#6043EF]">
            <FileArrowDown size={11} weight="bold" /> CSV
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-zinc-400 px-2 py-2 rounded-lg border border-zinc-100 bg-zinc-50">None</div>
      ) : (
        <div className="rounded-lg border border-zinc-200 overflow-x-auto">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500">
                {withStatus && <th className="text-left font-bold px-2 py-1.5 whitespace-nowrap">Status</th>}
                <th className="text-left font-bold px-2 py-1.5 whitespace-nowrap">Code</th>
                <th className="text-left font-bold px-2 py-1.5 whitespace-nowrap min-w-[160px]">College</th>
                {visCols.map((c) => (
                  <th key={c} className="text-right font-bold px-2 py-1.5 whitespace-nowrap">{FEE_LABELS[c] || c}</th>
                ))}
                {assignable && <th className="text-right font-bold px-2 py-1.5 whitespace-nowrap">Assign</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-zinc-50 align-top">
                  {withStatus && (
                    <td className="px-2 py-1.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${r.status === "new" ? "bg-emerald-50 text-emerald-700" : r.status === "skipped" ? "bg-zinc-200 text-zinc-600" : "bg-amber-50 text-amber-700"}`}>{r.status}</span>
                    </td>
                  )}
                  <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-500 whitespace-nowrap">{r.code || "—"}</td>
                  <td className="px-2 py-1.5 text-[#09090B]">{r.name || "—"}</td>
                  {visCols.map((c) => (
                    <td key={c} className="px-2 py-1.5 text-right font-mono text-[10px] whitespace-nowrap">{fmtFee(r[c])}</td>
                  ))}
                  {assignable && (
                    <td className="px-2 py-1.5 text-right">
                      {r.status === "unmatched"
                        ? <FeeAssign row={r} ingestId={ingestId} state={state} onReload={onReload} />
                        : <span className="text-[9px] text-zinc-300">—</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FeeAssign({ row, ingestId, state, onReload }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(row.name || "");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const QUOTA = ["govt_fees", "private_fees", "other_fees", "nri_fees"];
  const present = QUOTA.filter((c) => row[c] !== null && row[c] !== undefined && row[c] !== "");
  const [selected, setSelected] = useState(() => new Set(present));
  const toggle = (c) => {
    const next = new Set(selected);
    next.has(c) ? next.delete(c) : next.add(c);
    if (next.size === 0) return;
    setSelected(next);
  };
  const search = async (val) => {
    setQ(val);
    if (!val || val.trim().length < 2) { setResults([]); return; }
    try {
      const r = await adminCollegesLiteSearch(val.trim(), state);
      setResults((r.data?.results || r.data || []).slice(0, 8));
    } catch { setResults([]); }
  };
  const assign = async (code, name) => {
    setBusy(true);
    try {
      await assignDiaryFee(ingestId, code, row, Array.from(selected));
      toast.success(`Fees assigned to ${name || code}`);
      setOpen(false);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };
  if (!open) return (
    <button onClick={() => { setOpen(true); search(row.name || ""); }} data-testid="fee-assign-open"
      className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[#6043EF] text-white text-[9px] font-black uppercase tracking-wider hover:bg-[#4c34c9]">
      <MagnifyingGlass size={10} weight="bold" /> Assign
    </button>
  );
  return (
    <div className="mt-1 w-64 max-w-[70vw] text-left rounded-lg border border-[#6043EF]/30 bg-white p-2 shadow-lg" data-testid="fee-assign-panel" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1 mb-1.5">
        <input autoFocus value={q} onChange={(e) => search(e.target.value)} placeholder="Search college…" data-testid="fee-assign-input"
          className="flex-1 min-w-0 h-7 px-2 rounded border border-zinc-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
        <button onClick={() => setOpen(false)} className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100"><X size={12} weight="bold" /></button>
      </div>
      {present.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mb-1.5 px-1" data-testid="fee-assign-columns">
          <span className="text-[8px] font-black uppercase tracking-wider text-zinc-400">Columns</span>
          {present.map((c) => (
            <label key={c} className="inline-flex items-center gap-1 cursor-pointer select-none" data-testid={`fee-assign-col-${c}`}>
              <input type="checkbox" checked={selected.has(c)} onChange={() => toggle(c)} className="w-3 h-3 accent-[#059669] cursor-pointer" />
              <span className={`text-[9px] font-semibold ${selected.has(c) ? "text-[#09090B]" : "text-zinc-400 line-through"}`}>{FEE_LABELS[c] || c}</span>
            </label>
          ))}
        </div>
      )}
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {results.length === 0 && <div className="text-[10px] text-zinc-400 px-1 py-2">Type to search…</div>}
        {results.map((c) => {
          const code = c.college_code || c.code;
          const nm = c.short_name || c.primary_name || c.name;
          return (
            <button key={code} disabled={busy} onClick={() => assign(code, nm)} data-testid={`fee-assign-pick-${code}`}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-violet-50 disabled:opacity-50">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[9px] text-[#6043EF]">{code}</span>
                {c.state && <span className="text-[9px] text-zinc-400">· {c.state}</span>}
              </div>
              <div className="text-[10px] text-[#09090B] leading-tight">{nm}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FeesReport({ report, fileName, ingestId, state, onReload }) {
  const cols = report.columns || Object.keys(FEE_LABELS);
  const base = (fileName || "fees").replace(/\.[^.]+$/, "");
  return (
    <div className="space-y-4 pt-1" data-testid="diary-fees-report">
      <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">What the fees tool did</div>
      <FeesTable title="Matched & Updated" testid="fees-matched" accent="#059669"
        rows={report.matched || []} cols={cols} csvName={`${base}_matched.csv`} />
      <FeesTable title="Unmatched — assign to a college" testid="fees-new-unmatched" accent="#D97706" withStatus
        rows={report.new_unmatched || []} cols={cols} csvName={`${base}_new_unmatched.csv`}
        assignable ingestId={ingestId} state={state} onReload={onReload} />
    </div>
  );
}

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">{label}</div>
    <div className={`text-[13px] text-[#09090B] break-all ${mono ? "font-mono text-[11px]" : ""}`}>{value || "—"}</div>
  </div>
);

function Sel({ value, onChange, placeholder, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-7 px-2 rounded-lg border border-zinc-200 text-[11px] text-zinc-600 bg-white max-w-[150px]">
      <option value="">{placeholder}</option>
      {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  );
}

function fmtDate(d) {
  if (!d) return "Unknown date";
  try {
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
}


function UnmatchedAllotments({ body, year, round, onReload }) {
  const [rows, setRows] = useState(null);
  const [sugg, setSugg] = useState({});          // college_name_raw -> {suggested_code, suggested_name, city, score}
  const [llmBusy, setLlmBusy] = useState("");    // state currently being LLM-matched
  const [applying, setApplying] = useState("");  // raw currently being applied ("__bulk__" during bulk)
  const [selected, setSelected] = useState({});  // college_name_raw -> true (which suggestions to bulk-apply)
  const [stateFilter, setStateFilter] = useState(null);  // null → not yet defaulted
  const load = useCallback(async () => {
    try {
      const r = await listAllotmentUnmatched({ body, year, round_number: round });
      setRows(r.data?.unmatched || []);
    } catch { setRows([]); }
  }, [body, year, round]);
  useEffect(() => { load(); }, [load]);

  const { groups, stateNames } = useMemo(() => {
    const g = {};
    (rows || []).forEach((r) => { const st = (r.state || "").trim() || "Unknown"; (g[st] = g[st] || []).push(r); });
    // count-desc, but always push "Unknown" to the end
    const names = Object.keys(g).sort((a, b) =>
      (a === "Unknown") - (b === "Unknown") || g[b].length - g[a].length);
    return { groups: g, stateNames: names };
  }, [rows]);
  useEffect(() => {
    if (stateFilter === null && stateNames.length) setStateFilter(stateNames[0]);
  }, [stateNames, stateFilter]);

  const runLlm = async (state) => {
    setLlmBusy(state || "—");
    try {
      const r = await llmMatchAllotmentUnmatched({ counselling_body: body, year, round_number: round, state });
      const list = r.data?.suggestions || [];
      setSugg((prev) => {
        const next = { ...prev };
        list.forEach((s) => { next[s.college_name_raw] = s; });
        return next;
      });
      setSelected((prev) => {  // new suggestions are selected by default
        const next = { ...prev };
        list.forEach((s) => { next[s.college_name_raw] = true; });
        return next;
      });
      toast.success(`LLM matched ${list.length} of ${r.data?.checked || 0} in ${state || "—"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "LLM match failed");
    } finally { setLlmBusy(""); }
  };

  // Assign ONE row. Removes just that row locally (no full reload) so the other
  // LLM suggestions stay on screen. Returns true on success.
  const assignOne = async (raw, code) => {
    await assignAllotmentUnmatched({ counselling_body: body, year, round_number: round,
      college_name_raw: raw, college_code: code });
    setRows((prev) => (prev || []).filter((r) => r.college_name_raw !== raw));
    setSugg((prev) => { const n = { ...prev }; delete n[raw]; return n; });
    setSelected((prev) => { const n = { ...prev }; delete n[raw]; return n; });
    return true;
  };

  const applySuggestion = async (raw, s) => {
    setApplying(raw);
    try {
      await assignOne(raw, s.suggested_code);
      toast.success(`Assigned → ${s.suggested_name || s.suggested_code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(""); }
  };

  // Bulk-apply every SELECTED suggestion in the given rows (sequential — each also
  // refreshes that college's cutoffs server-side). No full page reload.
  const applyBulk = async (visibleRows) => {
    const targets = visibleRows.filter((r) => sugg[r.college_name_raw] && selected[r.college_name_raw]);
    if (!targets.length) { toast.error("Nothing selected"); return; }
    setApplying("__bulk__");
    let ok = 0, fail = 0;
    for (const r of targets) {
      try { await assignOne(r.college_name_raw, sugg[r.college_name_raw].suggested_code); ok += 1; }
      catch { fail += 1; }
    }
    setApplying("");
    toast.success(`Applied ${ok} suggestion${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
    onReload && onReload();  // refresh parent counts ONCE, after the whole batch
  };

  if (rows === null) return null;
  if (rows.length === 0) return null;

  const sel = stateFilter || stateNames[0];
  const list = groups[sel] || [];
  const selCount = list.filter((r) => sugg[r.college_name_raw] && selected[r.college_name_raw]).length;

  return (
    <div data-testid="diary-unmatched-allotments">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 flex items-center gap-1.5">
          Unmatched colleges — assign
          <span className="text-[8px] font-black uppercase bg-amber-100 text-amber-700 px-1 rounded">{rows.length}</span>
        </span>
        <select value={sel} onChange={(e) => setStateFilter(e.target.value)} data-testid="unmatched-state-filter"
          className="h-7 px-2 rounded-lg border border-zinc-200 text-[11px] bg-white ml-auto max-w-[220px]">
          {stateNames.map((st) => (<option key={st} value={st}>{st} ({groups[st].length})</option>))}
        </select>
        {sel !== "Unknown" && (
          <button onClick={() => runLlm(sel)} disabled={!!llmBusy} data-testid="llm-match-btn"
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-indigo-600 text-white text-[9px] font-black uppercase tracking-wider hover:bg-indigo-700 disabled:opacity-40">
            <MagicWand size={10} weight="fill" /> {llmBusy === sel ? "Matching…" : "LLM match"}
          </button>
        )}
        {selCount > 0 && (
          <button onClick={() => applyBulk(list)} disabled={applying === "__bulk__"} data-testid="apply-selected-btn"
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-emerald-600 text-white text-[9px] font-black uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-40">
            {applying === "__bulk__" ? "Applying…" : `Apply selected (${selCount})`}
          </button>
        )}
      </div>
      <p className="text-[10px] text-zinc-400 mb-2">Showing <b>{sel}</b> ({list.length}). Assign each to a college — or run <b>LLM match</b> to map this state's colleges one-to-one, tick the ones that look right and hit <b>Apply selected</b> (or Apply per row). Unclassified rows are under <b>Unknown</b>.</p>
      <div className="space-y-1">
        {list.map((r) => {
          const s = sugg[r.college_name_raw];
          const isSel = !!selected[r.college_name_raw];
          return (
            <div key={r.college_name_raw} className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/40 px-2 py-1.5" data-testid="unmatched-row">
              <div className="min-w-0 flex-1">
                <div className="text-[11px] text-[#09090B] font-semibold truncate" title={r.college_name_raw}>{r.name || r.college_name_raw}</div>
                <div className="text-[10px] text-zinc-500 flex items-center gap-1.5">
                  <span className="font-mono truncate">{r.college_name_raw}</span>
                  <span className="shrink-0 text-[8px] font-black uppercase bg-zinc-100 text-zinc-500 px-1 rounded">{r.count} rows</span>
                </div>
              </div>
              {s ? (
                <div className="shrink-0 flex items-center gap-2 max-w-[55%]" data-testid="unmatched-suggestion">
                  <input type="checkbox" checked={isSel} data-testid="unmatched-select"
                    onChange={(e) => setSelected((p) => ({ ...p, [r.college_name_raw]: e.target.checked }))}
                    className="h-4 w-4 accent-emerald-600 cursor-pointer" title="Include in Apply selected" />
                  <div className={`min-w-0 text-right ${isSel ? "" : "opacity-40"}`}>
                    <div className="text-[13px] font-bold text-emerald-700 leading-tight truncate" title={`${s.suggested_name} [${s.suggested_code}]`}>{s.suggested_name}</div>
                    <div className="text-[11px] text-zinc-600 truncate">{[s.city, s.state].filter(Boolean).join(", ") || "—"}</div>
                    <div className="text-[9px] text-zinc-400 truncate">{s.suggested_code}{s.score ? ` · ${s.score}%` : ""}</div>
                  </div>
                  <button onClick={() => applySuggestion(r.college_name_raw, s)} disabled={!!applying} data-testid="unmatched-apply-suggestion"
                    className="shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-full bg-emerald-600 text-white text-[9px] font-black uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-40">
                    {applying === r.college_name_raw ? "…" : "Apply"}
                  </button>
                </div>
              ) : (
                <div className="shrink-0">
                  <AllotAssign raw={r.college_name_raw} name={r.name} state={r.state}
                    body={body} year={year} round={round}
                    onDone={() => setRows((prev) => (prev || []).filter((x) => x.college_name_raw !== r.college_name_raw))} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AllotAssign({ raw, name, state, body, year, round, onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(name || "");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async (val) => {
    setQ(val);
    if (!val || val.trim().length < 2) { setResults([]); return; }
    try {
      // Unscoped search across ALL colleges (no state filter) so a mis-classified
      // row can still be attached to the correct master.
      const r = await adminCollegesLiteSearch(val.trim());
      setResults((r.data?.results || r.data || []).slice(0, 8));
    } catch { setResults([]); }
  };
  const assign = async (code, cname) => {
    setBusy(true);
    try {
      const r = await assignAllotmentUnmatched({ counselling_body: body, year, round_number: round,
        college_name_raw: raw, college_code: code });
      toast.success(`Assigned ${r.data?.restamped_rows || 0} rows to ${cname || code} · ${r.data?.summary_rows || 0} cutoff rows refreshed`);
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };
  if (!open) return (
    <button onClick={() => { setOpen(true); search(name || ""); }} data-testid="allot-assign-open"
      className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[#6043EF] text-white text-[9px] font-black uppercase tracking-wider hover:bg-[#4c34c9]">
      <MagnifyingGlass size={10} weight="bold" /> Assign
    </button>
  );
  return (
    <div className="mt-1 w-64 max-w-[70vw] rounded-lg border border-[#6043EF]/30 bg-white p-2 shadow-lg" data-testid="allot-assign-panel">
      <div className="flex items-center gap-1 mb-1.5">
        <input autoFocus value={q} onChange={(e) => search(e.target.value)} placeholder="Search college…" data-testid="allot-assign-input"
          className="flex-1 min-w-0 h-7 px-2 rounded border border-zinc-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
        <button onClick={() => setOpen(false)} className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100"><X size={12} weight="bold" /></button>
      </div>
      <div className="max-h-48 overflow-auto divide-y divide-zinc-100">
        {results.map((c) => (
          <button key={c.code || c.college_code} disabled={busy}
            onClick={() => assign(c.code || c.college_code, c.name || c.primary_name)}
            data-testid="allot-assign-result"
            className="w-full text-left px-1.5 py-1.5 hover:bg-[#6043EF]/5 disabled:opacity-50">
            <div className="text-[11px] font-semibold text-[#09090B] truncate">{c.name || c.primary_name}</div>
            <div className="text-[10px] text-zinc-500 truncate">{c.code || c.college_code} · {c.city || ""} {c.state || ""}</div>
          </button>
        ))}
        {q.trim().length >= 2 && results.length === 0 && <div className="px-1.5 py-2 text-[10px] text-zinc-400">No matches</div>}
      </div>
    </div>
  );
}
